<script>
  import { onMount, onDestroy } from "svelte";
  import { _, lget, locale } from "./i18n.js";
  import {
    f7,
    Page,
    Icon,
    Button,
    Stepper,
    Link,
    BlockTitle,
    Block,
    List,
    ListItem,
    SwipeoutActions,
    SwipeoutButton,
    Preloader
  } from "framework7-svelte";
  import Toolbar from "./Toolbar.svelte";
  import { getProfile } from "./UserService.js";
  import { addItem, getCartDoc, MAX_ITEMS } from "./CartService.js";
  import { getSecuredFirestore, getCachedFirst } from "./Firestore.js";
  import { capitalize } from "./Utils.js";

  export let f7route;
  let loading = true;
  let cart;
  let items = [];
  $: totalPrice = items
    .map(i => i.product.get("price") * i.amount)
    .reduce((a, b) => a + b, 0)
    .toLocaleString($locale, {
      minimumFractionDigits: 2
    });

  $: itemsLabel =
    cart === undefined
      ? ""
      : items.length == 0
      ? $_("cart.empty")
      : $_("cart.title", {
          values: {
            n: items.length
          }
        });

  let snapshotListener;

  onMount(async () => {
    getProfile().then(function(profile) {
      let getItems = async function(c) {
        cart = c;
        let i = c.get("items");

        // resolve all product refs
        const forLoop = async _ => {
          for (let index = 0; index < i.length; index++) {
            const item = i[index];
            let product = await getCachedFirst(item.productRef);
            item.product = product;
          }
        };
        await forLoop();

        loading = false;
        items = i;
      };

      let query = f7route.query;
      if (f7route.query.hasOwnProperty("addItemToCart")) {
        addItem(profile.userId, query.product, parseInt(query.amount)).then(
          function(cartDoc) {
            snapshotListener = cartDoc.onSnapshot(async function(cart) {
              getItems(cart);
            });
          }
        );
      } else {
        getCartDoc(profile.userId).then(function(cartDoc) {
          snapshotListener = cartDoc.onSnapshot(async function(cart) {
            getItems(cart);
          });
        });
      }
    });
  });

  onDestroy(async () => {
    snapshotListener && snapshotListener();
  });

  function updateItemCount(item, value) {
    item.amount = value;

    let safeItems = items.map(function(item) {
      let copy = { ...item };
      delete copy.product;
      return copy;
    });

    getSecuredFirestore().then(function(firestore) {
      firestore
        .collection("carts")
        .doc(cart.id)
        .update({
          items: safeItems
        });
    });
  }

  function removeItem(id) {
    let safeItems = items.filter(i => i.productRef.id !== id);
    safeItems = safeItems.map(function(item) {
      let copy = { ...item };
      delete copy.product;
      return copy;
    });

    getSecuredFirestore().then(function(firestore) {
      firestore
        .collection("carts")
        .doc(cart.id)
        .update({
          items: safeItems
        });
    });
  }

  function clearCart() {
    f7.dialog.confirm(
      $_("cart.clearCart.description"),
      $_("cart.clearCart.title"),
      () => {
        getSecuredFirestore().then(function(firestore) {
          firestore
            .collection("carts")
            .doc(cart.id)
            .update({
              items: []
            });
        });
      }
    );
  }

  function getUnitLabel(item) {
    if (item.amount == 1) {
      return capitalize(lget(item.product, "unit", $locale));
    } else {
      return capitalize(lget(item.product, "units", $locale));
    }
  }
</script>

<style>
  .logo {
    width: 80px;
  }
  .totalPrice {
    font-size: 1.2rem;
    font-weight: bold;
    margin-top: 5px;
  }
</style>

<Page>
  <div class="page-content">
    <div
      style="display: flex; align-items: center; justify-content: space-between;">
      <BlockTitle medium>{itemsLabel}</BlockTitle>
      {#if items.length > 0}
        <Button
          style="margin: 10px; font-size: 1.4rem; color: red"
          icon="icon icon-bin"
          on:click={function(e) {
            clearCart();
          }} />
      {/if}
    </div>

    {#if loading}
      <div
        style="align-items: center; display: flex; justify-content: center;
        flex-direction: column; height: 100%;">

        <Preloader />
      </div>
    {:else if items.length > 0}
      <List style="margin-top: 0px">
        {#each items as item}
          <ListItem
            swipeout
            header={lget(item.product, 'title', $locale)}
            title="{item.amount}
            {getUnitLabel(item)}">
            <SwipeoutActions right>
              <SwipeoutButton
                color="red"
                on:click={function(e) {
                  f7.dialog.confirm(
                    $_('cart.removeItem.description'),
                    $_('cart.removeItem.title'),
                    () => {
                      f7.swipeout.close(f7.swipeout.el, function() {
                        removeItem(item.productRef.id);
                      });
                    }
                  );
                }}>
                {$_('base.delete')}
              </SwipeoutButton>
            </SwipeoutActions>
            <span slot="after">
              <Stepper
                min={1}
                max={MAX_ITEMS}
                value={item.amount}
                buttonsOnly={false}
                small
                fill
                raised
                onStepperChange={function(value) {
                  if (value != item.amount) {
                    updateItemCount(item, value);
                  }
                }}
                autorepeat={true}
                autorepeatDynamic={true} />
            </span>
            <div slot="media">
              <img
                class="logo"
                alt={lget(item.product, 'title', $locale)}
                src={item.product.get('image')} />
            </div>
          </ListItem>
        {/each}

        <ListItem style="font-size: 1.2rem; font-weight: bold;">
          <div class="totalPrice" slot="title">{$_('cart.totalPrice')}</div>
          <div class="totalPrice" slot="after">{totalPrice} THB</div>
        </ListItem>
      </List>
    {:else}
      <Link href="/Products/" style="width: 100%;">
        <img
          alt="Empty Bottles"
          style="width: 100%; max-width: 600px; border-radius: 5px"
          src="https://lh3.googleusercontent.com/pw/ACtC-3fDbDYkui-je2oY415RYDBFnP7JvEdIP1RV36V_E8q9e-ocTkqp4iRzOw20dtngV37DPbtnAyatQiJ3a4-Xl6drpAC9-7qr2MTNPhmuHZ9LF6Jm-lf9vN45Z7twFy2hKinnIA-2s61fRUbglnhVIKKWaA=s800" />
      </Link>
    {/if}

    <div style="margin: 20px">
      <Button color="green" fill round href="/Products/">
        {#if items.length == 0}
          {$_('cart.button.addProducts')}
        {:else}{$_('cart.button.addMoreProducts')}{/if}
        <Icon
          style="margin-left: 5px; vertical-align: text-top"
          icon="icon-plus" />
      </Button>
    </div>

    {#if items.length > 0}
      <div style="margin: 20px">
        <Button fill round href="/Order/New">
          {$_('cart.button.order')}
          <Icon
            style="margin-left: 5px; vertical-align: text-top"
            icon="icon-cargo" />
        </Button>
      </div>
    {/if}
  </div>

  <Toolbar tabLinkActive={'/Cart'} />
</Page>
